/**
 * Manages a cache of active Sendbird channels to avoid UI blinks during navigation.
 * Uses LRU (Least Recently Used) eviction to limit memory usage.
 */
import type { GroupChannel } from '@sendbird/chat/groupChannel';

interface CacheEntry {
  channel: GroupChannel;
  lastAccessed: number;
}

class ChannelCacheManager {
  private cache: Map<string, CacheEntry> = new Map();

  private maxSize: number = 10; // Keep last 10 channels active

  /**
   * Get a channel from cache or fetch it if not cached.
   * Returns the cached channel to avoid re-fetching from Sendbird.
   */
  async getOrFetchChannel(channelUrl: string): Promise<GroupChannel> {
    const now = Date.now();

    // If cached, update access time and return
    if (this.cache.has(channelUrl)) {
      const entry = this.cache.get(channelUrl)!;
      entry.lastAccessed = now;
      return entry.channel;
    }

    // Lazy import to avoid side effects at module load
    const { fetchChannelByUrl } = await import('./sendbirdClient');
    const channel = await fetchChannelByUrl(channelUrl);

    // Add to cache
    this.cache.set(channelUrl, {
      channel,
      lastAccessed: now,
    });

    // Evict least recently used if over limit
    if (this.cache.size > this.maxSize) {
      this.evictLRU();
    }

    return channel;
  }

  /**
   * Evict the least recently used channel from cache.
   */
  private evictLRU(): void {
    let lruUrl: string | null = null;
    let lruTime = Infinity;

    this.cache.forEach((entry, url) => {
      if (entry.lastAccessed < lruTime) {
        lruTime = entry.lastAccessed;
        lruUrl = url;
      }
    });

    if (lruUrl) {
      this.cache.delete(lruUrl);
    }
  }

  /**
   * Clear the entire cache.
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics for debugging.
   */
  getStats(): { size: number; urls: string[] } {
    return {
      size: this.cache.size,
      urls: Array.from(this.cache.keys()),
    };
  }

  /**
   * Set the maximum cache size.
   */
  setMaxSize(size: number): void {
    this.maxSize = Math.max(1, size);
    // Trim cache if new max is smaller
    while (this.cache.size > this.maxSize) {
      this.evictLRU();
    }
  }
}

export default new ChannelCacheManager();
